/**
 * HairHelix3D — DNA-style double helix made of hair strands
 *
 * Visualizes "Client DNA" concept with intertwined golden strands
 * that rotate on scroll, revealing treatment history metaphorically.
 */

import { useRef, useState, useEffect, useMemo } from 'react'
import { Canvas, useFrame } from '@react-three/fiber'
import { Float, Environment } from '@react-three/drei'
import { EffectComposer, Bloom } from '@react-three/postprocessing'
import * as THREE from 'three'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import gsap from 'gsap'

gsap.registerPlugin(ScrollTrigger)

const GOLD = '#C9A84C'
const GOLD_LIGHT = '#d4b86a'
const STEEL_BLUE = '#005ea1'

interface HelixStrandProps {
  offset: number
  color: string
  radius?: number
}

/** Single helix strand */
function HelixStrand({ offset, color, radius = 0.8 }: HelixStrandProps) {
  const curve = useMemo(() => {
    const points: THREE.Vector3[] = []
    const turns = 3
    const height = 4

    for (let i = 0; i <= 100; i++) {
      const t = i / 100
      const angle = t * Math.PI * 2 * turns + offset
      points.push(
        new THREE.Vector3(Math.cos(angle) * radius, (t - 0.5) * height, Math.sin(angle) * radius)
      )
    }

    return new THREE.CatmullRomCurve3(points)
  }, [offset, radius])

  const geometry = useMemo(() => {
    return new THREE.TubeGeometry(curve, 100, 0.04, 8, false)
  }, [curve])

  return (
    <mesh geometry={geometry}>
      <meshStandardMaterial
        color={color}
        emissive={color}
        emissiveIntensity={0.3}
        metalness={0.6}
        roughness={0.3}
      />
    </mesh>
  )
}

/** Connector rungs between strands */
function HelixRungs({ count = 12 }: { count?: number }) {
  const rungs = useMemo(() => {
    const items: { y: number; angle: number }[] = []
    for (let i = 0; i < count; i++) {
      const t = i / (count - 1)
      items.push({
        y: (t - 0.5) * 4,
        angle: t * Math.PI * 2 * 3, // Match helix turns
      })
    }
    return items
  }, [count])

  return (
    <group>
      {rungs.map((rung, i) => (
        <mesh key={i} position={[0, rung.y, 0]} rotation={[0, rung.angle, 0]}>
          <boxGeometry args={[1.6, 0.02, 0.02]} />
          <meshStandardMaterial
            color={STEEL_BLUE}
            emissive={STEEL_BLUE}
            emissiveIntensity={0.2}
            transparent
            opacity={0.7}
          />
        </mesh>
      ))}
    </group>
  )
}

/** Data nodes along the helix */
function DataNodes({ count = 8 }: { count?: number }) {
  const nodes = useMemo(() => {
    const items: THREE.Vector3[] = []
    for (let i = 0; i < count; i++) {
      const t = (i + 0.5) / count
      const angle = t * Math.PI * 2 * 3
      items.push(new THREE.Vector3(Math.cos(angle) * 0.8, (t - 0.5) * 4, Math.sin(angle) * 0.8))
    }
    return items
  }, [count])

  return (
    <group>
      {nodes.map((pos, i) => (
        <mesh key={i} position={pos}>
          <sphereGeometry args={[0.06, 16, 16]} />
          <meshStandardMaterial
            color={i % 2 === 0 ? GOLD : STEEL_BLUE}
            emissive={i % 2 === 0 ? GOLD : STEEL_BLUE}
            emissiveIntensity={0.5}
          />
        </mesh>
      ))}
    </group>
  )
}

/** Complete helix assembly */
function HelixScene({ progress }: { progress: number }) {
  const groupRef = useRef<THREE.Group>(null)

  useFrame((state) => {
    if (groupRef.current) {
      // Continuous rotation + scroll boost
      groupRef.current.rotation.y = state.clock.elapsedTime * 0.2 + progress * Math.PI * 2
      // Mouse tilt
      groupRef.current.rotation.x = state.pointer.y * 0.2
      groupRef.current.rotation.z = state.pointer.x * 0.1
    }
  })

  return (
    <Float speed={1.5} rotationIntensity={0.2} floatIntensity={0.3}>
      <group ref={groupRef}>
        <HelixStrand offset={0} color={GOLD} />
        <HelixStrand offset={Math.PI} color={GOLD_LIGHT} />
        <HelixRungs count={14} />
        <DataNodes count={10} />
        <ambientLight intensity={0.5} />
        <directionalLight position={[3, 5, 5]} intensity={1.2} color="#fff5e6" />
        <pointLight position={[0, 0, 2]} intensity={0.6} color={GOLD} />
      </group>
    </Float>
  )
}

interface HairHelix3DProps {
  sectionRef?: React.RefObject<HTMLElement | null>
  scrollStart?: string
  scrollEnd?: string
  className?: string
}

export function HairHelix3D({
  sectionRef,
  scrollStart = 'top center',
  scrollEnd = 'bottom center',
  className = '',
}: HairHelix3DProps) {
  const [progress, setProgress] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const trigger = sectionRef?.current || containerRef.current?.parentElement
    if (!trigger) return

    const st = ScrollTrigger.create({
      trigger,
      start: scrollStart,
      end: scrollEnd,
      scrub: 1.5,
      onUpdate: (self) => setProgress(self.progress),
    })

    return () => st.kill()
  }, [sectionRef, scrollStart, scrollEnd])

  return (
    <div
      ref={containerRef}
      className={`pointer-events-none ${className}`}
      style={{ width: '100%', height: '100%' }}
      aria-hidden="true"
      data-testid="hair-helix-3d"
    >
      <Canvas
        camera={{ position: [0, 0, 5], fov: 40 }}
        dpr={[1, 2]}
        gl={{ alpha: true, antialias: true }}
      >
        <HelixScene progress={progress} />
        <Environment preset="sunset" />
        <EffectComposer enableNormalPass={false}>
          <Bloom luminanceThreshold={0.3} luminanceSmoothing={0.9} intensity={0.8} />
        </EffectComposer>
      </Canvas>
    </div>
  )
}

export default HairHelix3D
